import AnalyticsEvent = ToroAnalyticsEnums.AnalyticsEvent;

import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { finalize, take } from 'rxjs/operators';
import { I3AssetsMaintenanceDue, I3MaintenanceDueAsset } from '../../../../../api/intelli360/models/i3-assets-maintenance-due.model';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { AuthManagerService } from '../../../../../api/auth/auth-manager.service';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DashMessageService } from '../../../../../common/services/dash-message.service';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { DeviceManagerService } from '../../../../../common/services/device-manager.service';
import { environment } from '../../../../../../environments/environment';
import { HttpStatusCode } from '@angular/common/http';
import { Intelli360ManagerService } from '../../../../../api/intelli360/intelli360-manager.service';
import { Intelli360WidgetBase } from '../_intelli360-widget-base';
import { ToroAnalyticsEnums } from '../../../../../common/enumerations/analytics.enums';
import { TranslateService } from '@ngx-translate/core';
import { untilDestroyed } from '@ngneat/until-destroy';

@Component({
    selector: 'toro-widget-intelli360-maintenance',
    templateUrl: './widget-intelli360-maintenance.component.html',
    styleUrls: ['./widget-intelli360-maintenance.component.less']
})
export class WidgetIntelli360MaintenanceComponent extends Intelli360WidgetBase implements OnInit {
    @ViewChild('detailTableContainer', { static: false }) detailTableContainer: ElementRef<HTMLDivElement>

    iconColor = 'black';
    title = 'WIDGET.INTELLI360_MAINTENANCE';

    protected showDesktopModal = false;
    protected isDemoMode = false;
    protected assets: I3AssetsMaintenanceDue;
    protected maintenanceDueAssets: I3MaintenanceDueAsset[];
    protected verticalScrollbarWidth = 0;

    private _selectedAsset: I3MaintenanceDueAsset;
    protected set selectedAsset(value: I3MaintenanceDueAsset) {
        this._selectedAsset = value;
        setTimeout(() => this.checkForScrollbar());
    }

    protected get selectedAsset(): I3MaintenanceDueAsset {
        return this._selectedAsset;
    }

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(protected analyticsService: AnalyticsService,
                private authManager: AuthManagerService,
                protected broadcastService: BroadcastService,
                private dashMessageService: DashMessageService,
                protected dashUserManager: DashUserManagerService,
                protected deviceManager: DeviceManagerService,
                protected i3Manager: Intelli360ManagerService,
                private intelli360Manager: Intelli360ManagerService,
                protected translateService: TranslateService,
    ) {
        super(analyticsService, broadcastService, dashUserManager, deviceManager, i3Manager, translateService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.deviceManager.windowResize
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.checkForScrollbar();
            });

        this.isDemoMode = environment.isDemoMode;
        if (this.isDemoMode) { this.title = ''; }

        // NOTE: Initial data is fetched in the base class call to super.widgetResized. This is to ensure we don't attempt to load
        // any widget content until the widget has been properly sized (i.e., displayCols/displayRows has been properly set.
    }

    private checkForScrollbar(resetScrollbar: boolean = false): void {
        if (this.detailTableContainer == null) { return; }

        const element = this.detailTableContainer.nativeElement;
        this.verticalScrollbarWidth = element.offsetWidth - element.clientWidth;

        if (resetScrollbar) {
            this.detailTableContainer.nativeElement.scrollTop = 0;
        }
    }

    // =========================================================================================================================================================
    // Base Class Overrides
    // =========================================================================================================================================================

    get analyticsWidgetName(): string {
        return AnalyticsEvent.Intelli360MaintenanceWidgetName;
    }

    protected getWidgetData() {
        if (this.isDemoMode) {
            // In demo mode, we do not fetch real data. We show an image.
            this.isBusy = false;
            return;
        }

        if (!this.isIntelli360UserLoggedIn) { return; }
        if (this.authManager.isDoingIntelli360Login) { return; }
        if (this.isUnableToFetchData) { this.isBusy = true; }

        this.intelli360Manager.getMaintenanceDueAssets()
            .pipe(
                take(1),
                finalize(() => {
                    if (this.selectedAsset == null && this.maintenanceDueAssets.length > 0) { this.selectedAsset = this.maintenanceDueAssets[0]; }
                    this.isWidgetInitialized = true;
                    this.isBusy = false;
                })
            )
            .subscribe({
                next: (response: I3AssetsMaintenanceDue) => {
                    this.assets = response;

                    // TODO - VERIFY THIS IS CORRECT
                    // Filter out maintenance tasks with no nextDueInDate or NoMaintenanceDue status.
                    // this.assets.maintenanceDueAssetListModel = response.maintenanceDueAssetListModel.filter(a => {
                    //     return a.taskStatus != I3TaskStatus.NoMaintenanceDue && a.nextDueInDate >= new Date('01-01-01')
                    // });

                    this.maintenanceDueAssets = Object.values(
                        this.assets.maintenanceDueAssetListModel.reduce((acc, task) => {
                            if (!acc[task.assetId]) {
                                acc[task.assetId] = new I3MaintenanceDueAsset({
                                    assetId: task.assetId,
                                    tasks: []
                                });
                            }
                            acc[task.assetId].tasks.push(task);
                            return acc;
                        }, {} as { [assetId: number]: I3MaintenanceDueAsset })
                    );

                    if (this.maintenanceDueAssets.length == 0) { this.selectedAsset = null; }

                    this.lastUpdateTimestamp = new Date();
                    this.clearIsUnableToFetchData();
                }, error: err => {
                    this.isIntelli360UserLoggedIn = err.status !== HttpStatusCode.FailedDependency;
                    if (!this.isIntelli360UserLoggedIn) { this.intelli360UserName = ''; }

                    this.setIsUnableToFetchData(!this.isIntelli360UserLoggedIn ? 'ERR_MSG.I3_USER_NOT_LOGGED_IN' : 'ERR_MSG.SERVER_ERROR');
                    if (this.isWidgetInitialized) { this.dashMessageService.showWidgetDataFetchErrorMessage(this.title); }
                }
            });
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onLinkClick() {
        this.broadcastService.toggleSystemOverlay.next({ show: true, text: 'STRINGS.NAVIGATING_TO_INTELLI360' });
        setTimeout(() => window.open(environment.intelli360SiteUrl, '_blank'), 1000);
        setTimeout(() => this.broadcastService.toggleSystemOverlay.next({ show: false }), 2000);
    }

}
